import React, { useEffect, useRef } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

import ProfileEditInput from 'app/components/CommonStyled/ProfileEditInput';
import { Form, Formik, FormikProps } from 'formik';
import { postLinkedin } from 'services/profile-editor';
import {
  profileDataSelector,
  setLinkedin,
} from 'store/reducers/profile-editor/profile-editor-reducer';
import * as Yup from 'yup';

import ProfileEditorDrawer, { ProfileEditorDrawerRefProps } from '../Drawer';
import { ProfileEditorDrawerTitle } from '../styled';
import { ProfileEditorDrawerLinkedinWrapper } from './styled';

interface ProfileEditorDrawerLinkedinProps {
  onClose: () => void;
}

export default function ProfileEditorDrawerLinkedin({ onClose }: ProfileEditorDrawerLinkedinProps) {
  const dispatch = useDispatch();
  const formikRef = useRef<FormikProps<any>>(null);
  const drawerRef = useRef<ProfileEditorDrawerRefProps>(null);
  const { linkedin } = useSelector(profileDataSelector);

  useEffect(() => {
    if (linkedin) {
      formikRef.current.setFieldValue('linkedin', linkedin);
    }
  }, [linkedin]);

  const handlePostLinkedin = async (params: any) => {
    const response = await postLinkedin(params);
    if (!response.ok) {
      throw new Error(response.originalError?.message);
    }

    dispatch(setLinkedin(params.value));

    return response.data;
  };

  const { mutate } = useMutation(handlePostLinkedin, drawerRef.current?.handleMutation);

  const LinkedinValidationSchema = Yup.object().shape({
    linkedin: Yup.string().matches(
      /^(https?:\/\/)?(www\.)?linkedin\.com\/(in|company)\/[a-zA-Z0-9-]{3,}\/?$/,
      'Looks like this isn’t a valid URL, check for any typos or try another URL'
    ),
  });

  const handleSubmit = (values) => {
    mutate({ value: values.linkedin });
  };

  return (
    <Formik
      initialValues={{
        linkedin,
      }}
      validationSchema={LinkedinValidationSchema}
      onSubmit={handleSubmit}
      innerRef={formikRef}
      validateOnChange={false}
      validateOnBlur={true}
    >
      {(props) => (
        <Form>
          <ProfileEditorDrawer
            ref={drawerRef}
            header={<ProfileEditorDrawerLinkedinHeader />}
            needsSaving={props.dirty}
            onClose={onClose}
          >
            <ProfileEditorDrawerLinkedinWrapper>
              <ProfileEditInput
                {...props}
                rows={1}
                name={'linkedin'}
                label={'Add your LinkedIn profile URL'}
                type={'text'}
                placeholder={'https://www.linkedin.com/...'}
              />
            </ProfileEditorDrawerLinkedinWrapper>
          </ProfileEditorDrawer>
        </Form>
      )}
    </Formik>
  );
}

function ProfileEditorDrawerLinkedinHeader() {
  return <ProfileEditorDrawerTitle>LinkedIn</ProfileEditorDrawerTitle>;
}
